import { URLList } from "@/data/url_list";
import { toStoreError } from "@/store/store-error";
import { formatMaskedPhone } from "@/store/store-format";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { SATError, SATErrorModel, SATResult } from "@sadtx/sat-error";
import {
  StoreCreateOrderResponseDto,
  StoreGuardianSearchEnvelopeDto,
  StoreGuardianSearchResponseDto,
  StorePatientContextResponseDto,
  StorePaymentHistoryPageDto,
  StoreProductResponseDto,
  StorePurchaseResponseDto,
  StoreOrderReceiptResponseDto,
  StoreRefundQuoteResponseDto,
  StoreRefundRequestResponseDto,
  StoreRefundResponseDto,
  StoreSettlementPageDto,
  RevokeTreatmentVoucherResponseDto,
  TreatmentVoucherClaimResponseDto,
  VoucherRefundAttestationResponseDto,
  StoreBrandpayCustomerResponseDto,
  StoreBrandpayMethodsResponseDto,
} from "../dto/store-response-dtos";
import {
  StoreConfirmPurchaseRequestPayload,
  StoreGuardianSearchRequestPayload,
  StorePaymentHistoryRequestPayload,
  StorePrepareOrderRequestPayload,
  StoreRefundRequestCreatePayload,
  StoreRefundRequestDecisionPayload,
  StoreRefundRequestPayload,
  StoreSettlementRequestPayload,
  RevokeTreatmentVoucherRequestPayload,
  TreatmentVoucherClaimRequestPayload,
  VoucherOrderIdRequestPayload,
} from "../payload/store-request-payloads";
import { StoreHttpClientMethod } from "../store-http-client";
import { StoreURLList } from "../store-url-list";
import { StoreSource } from "./store-source";

/** "01012345678" / "010-1234-5678" → "010-****-5678" (표시용 마스킹) */
/**
 * 이용권 스토어 실제 API 소스 — 클라이언트 2개를 쓴다.
 *  - medicalClient: 의료 서버 (기존 FetchApiClient — 쿠키·/s/ 암호화). 환자 컨텍스트·보호자 검색
 *  - storeClient  : store-api (스토어 전용 토큰 Bearer, 암호화 채널 없음). 상품·주문·결제·이용권·내역·정산
 */
export class StoreApiSource implements StoreSource {
  constructor(
    private readonly medicalClient: FetchApiClientMethod,
    private readonly storeClient: StoreHttpClientMethod
  ) {}

  async fetchProducts(): Promise<SATResult<StoreProductResponseDto[], SATError>> {
    try {
      const response = await this.storeClient.get<StoreProductResponseDto[]>(StoreURLList.PRODUCT.LIST);
      console.log("[StoreApiSource] :: fetchProducts :: 상품 목록 조회 성공");
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: fetchProducts :: 상품 목록 조회 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async fetchBrandpayCustomer(): Promise<SATResult<StoreBrandpayCustomerResponseDto, SATError>> {
    try {
      const response = await this.storeClient.get<StoreBrandpayCustomerResponseDto>(
        StoreURLList.BRANDPAY.CUSTOMER
      );
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: fetchBrandpayCustomer :: 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async fetchBrandpayMethods(): Promise<SATResult<StoreBrandpayMethodsResponseDto, SATError>> {
    try {
      const response = await this.storeClient.get<StoreBrandpayMethodsResponseDto>(
        StoreURLList.BRANDPAY.METHODS
      );
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: fetchBrandpayMethods :: 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async resolvePatientContext(
    patientUuid: string
  ): Promise<SATResult<StorePatientContextResponseDto, SATError>> {
    try {
      const response = await this.medicalClient.get(
        `${URLList.TREATMENT.GET_TREATMENT_LIST}/${patientUuid}`
      );
      const dto = response.data as {
        patient?: {
          fullName?: string | null;
          firstName?: string | null;
          isDeactivated?: boolean;
          /** 서버는 "YYYY-MM-DD" 문자열로 준다 */
          birthDate?: string | null;
        };
        guardian?: { fullName: string; phone: string; displayPhone?: string };
        voucher?: { months: number; orderId: string } | null;
      };

      const patientName = dto.patient?.fullName ?? dto.patient?.firstName ?? "";
      if (!patientName) {
        return SATResult.failure(
          new SATErrorModel.FailToGetItem("환자 정보를 확인할 수 없습니다.")
        );
      }
      if (dto.patient?.isDeactivated) {
        return SATResult.failure(
          new SATErrorModel.FailToGetItem("비활성화된 환자입니다. 환자 상태를 확인해주세요.")
        );
      }
      if (!dto.guardian) {
        return SATResult.failure(
          new SATErrorModel.FailToGetItem("보호자 정보가 없습니다. 보호자 등록 후 이용해주세요.")
        );
      }

      console.log("[StoreApiSource] :: resolvePatientContext :: 환자 컨텍스트 조회 성공");
      return SATResult.success({
        // 보호자 uuid 는 이 API 응답에 없다 — 실제 구매 API 는 patientUuid 로 보호자를
        // 서버에서 재검증하므로 클라이언트 식별자는 표시 목적으로만 쓴다.
        guardianUuid: `guardian-of-${patientUuid}`,
        guardianName: dto.guardian.fullName,
        // 서버는 E.164 원형("+8210…")만 준다 → "(+82) 010-****-5919"
        guardianMaskedPhone: formatMaskedPhone(dto.guardian.phone || dto.guardian.displayPhone),
        patientUuid,
        patientName,
        patientBirthDate: dto.patient?.birthDate ?? null,
        hasUnusedVoucher: dto.voucher != null,
      });
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: resolvePatientContext :: 조회 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async searchGuardian(
    payload: StoreGuardianSearchRequestPayload
  ): Promise<SATResult<StoreGuardianSearchResponseDto | null, SATError>> {
    try {
      // 의료 서버 응답은 { guardian: ... | null } 봉투 (암호화 인터셉터가 null 본문을 다루지 못함)
      const response = await this.medicalClient.post(StoreURLList.GUARDIAN.SEARCH, payload);
      const envelope = response.data as StoreGuardianSearchEnvelopeDto;
      console.log("[StoreApiSource] :: searchGuardian :: 보호자 검색 성공", !!envelope?.guardian);
      return SATResult.success(envelope?.guardian ?? null);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: searchGuardian :: 보호자 검색 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async prepareOrder(
    payload: StorePrepareOrderRequestPayload
  ): Promise<SATResult<StoreCreateOrderResponseDto, SATError>> {
    try {
      const response = await this.storeClient.post<StoreCreateOrderResponseDto>(
        StoreURLList.PURCHASE.PREPARE,
        payload
      );
      console.log("[StoreApiSource] :: prepareOrder :: 주문 생성 성공");
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: prepareOrder :: 주문 생성 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async confirmPurchase(
    payload: StoreConfirmPurchaseRequestPayload
  ): Promise<SATResult<StorePurchaseResponseDto, SATError>> {
    try {
      const response = await this.storeClient.post<StorePurchaseResponseDto>(
        StoreURLList.PURCHASE.CONFIRM,
        payload
      );
      console.log("[StoreApiSource] :: confirmPurchase :: 결제 승인+이용권 발급 성공");
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: confirmPurchase :: 결제 승인 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async claimTreatmentVoucher(
    payload: TreatmentVoucherClaimRequestPayload
  ): Promise<SATResult<TreatmentVoucherClaimResponseDto, SATError>> {
    try {
      // 의료 서버 — 로그인 쿠키·/s/ 암호화 채널
      const response = await this.medicalClient.post(StoreURLList.VOUCHER.CLAIM, payload);
      console.log("[StoreApiSource] :: claimTreatmentVoucher :: 이용권 자격 발급 성공");
      return SATResult.success(response.data as TreatmentVoucherClaimResponseDto);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: claimTreatmentVoucher :: 자격 발급 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async markOrderClaimed(orderId: string): Promise<SATResult<void, SATError>> {
    try {
      await this.storeClient.patch(StoreURLList.VOUCHER.CLAIMED(orderId));
      return SATResult.success(undefined);
    } catch (error: unknown) {
      // 표시용이라 실패해도 플로우는 계속 — 로그만
      console.warn("[StoreApiSource] :: markOrderClaimed :: 표시 갱신 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async fetchOrderReceipt(
    orderId: string
  ): Promise<SATResult<StoreOrderReceiptResponseDto, SATError>> {
    try {
      const response = await this.storeClient.get<StoreOrderReceiptResponseDto>(
        StoreURLList.VOUCHER.RECEIPT(orderId)
      );
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: fetchOrderReceipt :: 영수증 재발급 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async requestVoucherRefundAttestation(
    payload: VoucherOrderIdRequestPayload
  ): Promise<SATResult<VoucherRefundAttestationResponseDto, SATError>> {
    try {
      const response = await this.medicalClient.post(StoreURLList.REFUND.ATTESTATION, payload);
      return SATResult.success(response.data as VoucherRefundAttestationResponseDto);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: requestVoucherRefundAttestation :: 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async cancelVoucherRefund(payload: VoucherOrderIdRequestPayload): Promise<SATResult<void, SATError>> {
    try {
      await this.medicalClient.post(StoreURLList.REFUND.CANCEL, payload);
      return SATResult.success(undefined);
    } catch (error: unknown) {
      console.warn("[StoreApiSource] :: cancelVoucherRefund :: 잠금 해제 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async quoteRefund(
    orderId: string,
    payload: StoreRefundRequestPayload
  ): Promise<SATResult<StoreRefundQuoteResponseDto, SATError>> {
    try {
      const response = await this.storeClient.post<StoreRefundQuoteResponseDto>(
        StoreURLList.REFUND.QUOTE(orderId),
        payload
      );
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: quoteRefund :: 견적 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async createRefundRequest(
    orderId: string,
    payload: StoreRefundRequestCreatePayload
  ): Promise<SATResult<StoreRefundRequestResponseDto, SATError>> {
    try {
      const response = await this.storeClient.post<StoreRefundRequestResponseDto>(
        StoreURLList.REFUND.REQUEST(orderId),
        payload
      );
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: createRefundRequest :: 요청 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async fetchRefundRequests(
    status: string | null
  ): Promise<SATResult<StoreRefundRequestResponseDto[], SATError>> {
    try {
      const url = status
        ? `${StoreURLList.REFUND.ADMIN_REQUESTS}?status=${encodeURIComponent(status)}`
        : StoreURLList.REFUND.ADMIN_REQUESTS;
      const response = await this.storeClient.get<StoreRefundRequestResponseDto[]>(url);
      return SATResult.success(response.data ?? []);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: fetchRefundRequests :: 조회 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async decideRefundRequest(
    requestId: number,
    payload: StoreRefundRequestDecisionPayload
  ): Promise<SATResult<StoreRefundRequestResponseDto, SATError>> {
    try {
      const response = await this.storeClient.patch<StoreRefundRequestResponseDto>(
        StoreURLList.REFUND.ADMIN_DECIDE(requestId),
        payload
      );
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: decideRefundRequest :: 결정 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async refundOrder(
    orderId: string,
    payload: StoreRefundRequestPayload
  ): Promise<SATResult<StoreRefundResponseDto, SATError>> {
    try {
      const response = await this.storeClient.post<StoreRefundResponseDto>(
        StoreURLList.REFUND.ORDER(orderId),
        payload
      );
      console.log("[StoreApiSource] :: refundOrder :: 환불 완료", orderId);
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: refundOrder :: 환불 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async revokeTreatmentVoucher(
    payload: RevokeTreatmentVoucherRequestPayload
  ): Promise<SATResult<RevokeTreatmentVoucherResponseDto, SATError>> {
    try {
      const response = await this.medicalClient.post(StoreURLList.REFUND.REVOKE, payload);
      return SATResult.success(response.data as RevokeTreatmentVoucherResponseDto);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: revokeTreatmentVoucher :: 자격 회수 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async fetchPaymentHistory(
    payload: StorePaymentHistoryRequestPayload
  ): Promise<SATResult<StorePaymentHistoryPageDto, SATError>> {
    try {
      const params = new URLSearchParams({
        page: String(payload.page),
        size: String(payload.size),
      });
      if (payload.status) {
        params.set("status", payload.status);
      }
      if (payload.query) {
        params.set("q", payload.query);
      }
      const response = await this.storeClient.get<StorePaymentHistoryPageDto>(
        `${StoreURLList.PAYMENT_HISTORY.LIST}?${params}`
      );
      console.log("[StoreApiSource] :: fetchPaymentHistory :: 결제 내역 조회 성공");
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: fetchPaymentHistory :: 결제 내역 조회 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }

  async fetchSettlements(
    payload: StoreSettlementRequestPayload
  ): Promise<SATResult<StoreSettlementPageDto, SATError>> {
    try {
      const response = await this.storeClient.get<StoreSettlementPageDto>(
        `${StoreURLList.SETTLEMENT.LIST}?month=${encodeURIComponent(payload.month)}`
      );
      console.log("[StoreApiSource] :: fetchSettlements :: 정산 조회 성공");
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.error("[StoreApiSource] :: fetchSettlements :: 정산 조회 실패", error);
      return SATResult.failure(toStoreError(error));
    }
  }
}
